import { Option } from '@core/model/framework/Result/Option';
import { ConnectionFormItemDto } from '@core/service/digital-factory/bank/types/dto/connectionFormDto';

/*
  The relay page carries its journey data in the URL FRAGMENT (after the #),
  not in sessionStorage: a fragment survives window.open AND a webview loading
  it in place, whereas the sessionStorage of a third-party iframe is
  partitioned away (verified on a real WKWebView). A fragment is never sent to
  the server (no access log, no referer), and the relay purges it once read.

  The `autonomous` fragment holds the user token and lets the relay complete
  the call itself (mobile / plain flow), deciding its completion mode from the
  opener. It also carries the correlation key the origin chose for the journey:
  the relay hands it to webauth-url, so the origin — which never hears from the
  relay — can poll the journey status by that key.

  The ORIGIN puts no survival hint in the fragment: whether it lives to poll
  is not something it can know when it builds this (it cannot foresee what
  the partner will do on open). The PARTNER may: a native app that opens this
  page in an external browser while keeping its webview — the origin — alive
  declares it AT THAT MOMENT by appending `&originSurvives=1` to the url it
  opens (the fragment never reaches a server, and forging it is harmless: at
  worst the journey lands on the widget outcome page). buildAutonomousRelayFragment
  never emits it; only parsing knows it.
*/
export type WebauthRelayFragment =
  | {
      kind: 'autonomous';
      token: string;
      bankId: string;
      journeyKey: string;
      form: Option<ConnectionFormItemDto[]>;
      /* Appended by the partner's native layer, never by the origin. */
      originSurvives: boolean;
    }
  | { kind: 'invalid' };

export type BuildAutonomousFragmentInput = {
  token: string;
  bankId: string;
  journeyKey: string;
  regionalForm: Option<{ fieldName: string; websiteUrl: string }>;
};

export function buildAutonomousRelayFragment(input: BuildAutonomousFragmentInput): string {
  const params = new URLSearchParams({
    token: input.token,
    bankId: input.bankId,
    journeyKey: input.journeyKey,
  });
  if (input.regionalForm != null) {
    params.set('regionalField', input.regionalForm.value.fieldName);
    params.set('regionalUrl', input.regionalForm.value.websiteUrl);
  }
  return `#${params.toString()}`;
}

export function parseWebauthRelayFragment(hash: string): WebauthRelayFragment {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const params = new URLSearchParams(raw);

  const token = params.get('token');
  const bankId = params.get('bankId');
  const journeyKey = params.get('journeyKey');
  if (token && bankId && journeyKey) {
    const regionalField = params.get('regionalField');
    const regionalUrl = params.get('regionalUrl');
    const form: Option<ConnectionFormItemDto[]> =
      regionalField && regionalUrl ? { value: [{ name: regionalField, value: regionalUrl }] } : null;

    return {
      kind: 'autonomous',
      token,
      bankId,
      journeyKey,
      form,
      originSurvives: params.get('originSurvives') === '1',
    };
  }

  return { kind: 'invalid' };
}
